<template>
  <div
    class="h-full pt-7 flex flex-col justify-between z-40 bg-neutral-white overflow-auto"
    data-seasonal-drawer
    v-bind="$attrs"
    :class="{
      'w-[63px]': collapsed,
    }"
  >
    <div>
      <slot name="close" />
      <section
        :class="{
          'px-5': !collapsed,
          'px-2': collapsed,
        }"
      >
        <div class="flex items-center justify-between mb-4 flex-row-reverse">
          <div class="flex items-center gap-2">
            <PulseOptionsMenu
              :position="isNotificationsEnabled ? '-right-8' : 'left'"
              :options="accountMenuOptions"
              @account="goToAccount"
              @pat="goToPersonalAccessTokens"
              @impersonate="impersonationPickerOpen = true"
              @logout="logout"
            >
              <UserAvatar v-show="!collapsed" :image="picture" :initials="initials" />
            </PulseOptionsMenu>
            <Notification v-show="!collapsed" />
          </div>
          <SeasonalLogo />
        </div>
        <section class="mb-8">
          <SearchInput class="mb-4" />
          <StoreSelectButton @store-select:click="$emit('store-select:click')" />
        </section>
      </section>
      <section class="px-2">
        <ClientOnly>
          <NavItems @menu:click="$emit('menu:click')" />
        </ClientOnly>
      </section>
    </div>
    <div class="py-8">
      <NavItem
        v-if="!isGlobalSupport && isSingleStoreSelected"
        v-can="[can.macro.read]"
        :to="`/${currentStore?.slug}/macros`"
      >
        <component :is="macrosIcon" class="h-5 w-5" />
        <span v-if="!collapsed">Macros</span>
      </NavItem>
      <ThemeSwitch class="text-xsm px-4" :collapsed="collapsed" />
    </div>
    <ImpersonationTargetModal :open="impersonationPickerOpen" @close="impersonationPickerOpen = false" />
  </div>
</template>

<script lang="ts" setup>
import { FEATURE_FLAGS } from '@constants';
import { ENTITLEMENTS } from '@auth';
import { hasAccountEntitlements } from '~/composables/useEntitlements';
import * as Icons from '@pulse/components/icons';

import { seasonalNavIcon } from '~/composables/useSeasonalIcons';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '~/store/auth';
import { tap } from 'rxjs';

import { useMerchantStore } from '~/store/merchant';
import { useUserStore } from '~/store/user';
import { useImpersonationStore } from '~/store/impersonation';

const macrosIcon = computed(() => seasonalNavIcon('Macros', Icons.LineBolt));

const merchantStore = useMerchantStore();
const { getFlag } = useLaunchDarkly();

const { currentStore } = storeToRefs(merchantStore);
const isNotificationsEnabled = computed(() => getFlag(FEATURE_FLAGS.NOTIFICATIONS, false));

const accountMenuOptions = computed(() =>
  [
    { color: 'text-typography-primary', icon: 'LineUsersAlt', label: 'Account', value: 'account' },
    getFlag(FEATURE_FLAGS.DEVELOPER_PAT, false) && {
      color: 'text-typography-primary',
      icon: 'ApiLogo',
      label: 'Personal access tokens',
      value: 'pat',
    },
    canImpersonate.value && {
      color: 'text-typography-primary',
      icon: 'LineEye',
      label: 'Impersonate',
      value: 'impersonate',
    },
    { color: 'text-typography-primary', icon: 'LineMinusCircle', label: 'Logout', value: 'logout' },
  ].filter(Boolean),
);

// Gated on the engineering:impersonate entitlement (engineering + implementation roles), flag-gated, and hidden
// while a session is already live (Exit lives in the banner).
const impersonation = useImpersonationStore();
const canImpersonate = computed(
  () =>
    hasAccountEntitlements([ENTITLEMENTS.engineering.impersonate]) &&
    getFlag(FEATURE_FLAGS.ENGINEER_IMPERSONATION, false) &&
    !impersonation.active,
);

const impersonationPickerOpen = ref(false);

defineProps({
  open: {
    default: false,
    type: Boolean,
  },
});

defineEmits(['menu:click', 'store-select:click']);

const store = useUserStore();
const auth = useAuthStore();

const { isGlobalSupport } = storeToRefs(store);

function logout() {
  auth
    .logout()
    .pipe(tap(() => navigateTo('/login')))
    .subscribe();
}

function goToAccount() {
  navigateTo('/account');
}

function goToPersonalAccessTokens() {
  navigateTo('/personal-access-tokens');
}

const picture = computed(() => store.user?.picture);
const isSingleStoreSelected = computed(() => !!currentStore.value && !Array.isArray(currentStore.value));
const collapsed = inject('collapsed', true);
const initials = computed(() => {
  const first = store.user?.firstName?.[0];
  const last = store.user?.lastName?.[0];

  if (!first || !last) {
    return undefined;
  }

  return first + last;
});
</script>
